import type { FC } from "react";
import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDebounce, useSearchForLocation } from "shared/hooks";

import type { LocationInputModel } from "shared/types";
import { ImageSizesEnum, Img, Input, Text } from "shared/uikit";
import type { InputWrapperProps } from "shared/uikit/Input";
import type { SearchProps } from "shared/uikit/Input/Search";
import { TextVariantsEnum } from "shared/uikit/types";

interface Props extends Omit<SearchProps, "variant" | "onChange" | "error" | "options">, InputWrapperProps {
	onChange: (val: string) => void;
	error?: string;
	hideHeadline?: boolean;
}

const LocationInput: FC<Props> = ({ onChange, value, error, placeholder, hideHeadline, ...rest }) => {
	const { t } = useTranslation();

	const [locationQuery, setLocationQuery] = useState("");
	const debouncedLocationQuery = useDebounce(locationQuery, 500);

	const places = useSearchForLocation(debouncedLocationQuery);

	const renderLocation = useCallback(
		(location: LocationInputModel) => (
			<div className="flex items-center w-full gap-4">
				<Img src={location.photo} alt={location.label} size={ImageSizesEnum.Small32} circle />
				<div className="flex items-center flex-1 gap-2 overflow-hidden">
					<Text variants={TextVariantsEnum.BodySmall} className="font-bold flex-shrink-0">
						{location.label}
					</Text>
					<Text variants={TextVariantsEnum.BodySmall} className="text-gray-400 flex-1 break-words truncate">
						{location.address}
					</Text>
				</div>
			</div>
		),
		[]
	);

	return (
		<Input
			search
			onInputChange={(_, val) => setLocationQuery(val)}
			value={value || null}
			onChange={(_, val) => onChange(val || "")}
			placeholder={placeholder || t("enter_address")}
			headline={!hideHeadline ? t("location") : undefined}
			options={places}
			error={!!error}
			errorText={error}
			customDropdownItemRender={renderLocation}
			dropdownMaxHeight={190}
			{...rest}
		/>
	);
};

export default LocationInput;
